import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCheck, IconDownload, IconSearch } from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import OutputFormatSelect from '../../image/shared/OutputFormatSelect';
import { PAGE_OUTPUT_FORMATS, encodeCanvas } from '../../image/shared/imageOutputFormats';
import { deriveToolStatus, downloadBlob, genericHowToSteps, getToolStatusText } from '../../toolPageUtils';
import { BD_DISTRICTS, BD_DIVISIONS } from './bdTravelMapData.js';
import {
    CARD,
    MAP_PLACEMENT,
    drawTravelMap,
    renderTravelMapCanvas,
} from './bdTravelMapDraw.js';

const STORAGE_KEY = 'sohozkaj:bd-travel-map:v1';
const TOTAL = BD_DISTRICTS.length;
const BY_DIVISION = BD_DIVISIONS.map((division) => ({
    ...division,
    districts: BD_DISTRICTS.filter((d) => d.division === division.id),
}));

function readSaved() {
    try {
        const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        if (!saved) return null;
        const known = new Set(BD_DISTRICTS.map((d) => d.id));
        return {
            visited: new Set((saved.visited || []).filter((id) => known.has(id))),
            name: typeof saved.name === 'string' ? saved.name.slice(0, 40) : '',
            showNames: saved.showNames !== false,
        };
    } catch {
        return null;
    }
}

export default function BangladeshTravelMapGeneral() {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'en' ? 'en' : 'bn';
    const num = useCallback((n) => (lang === 'bn' ? toBnDigits(n) : String(n)), [lang]);

    const [visited, setVisited] = useState(() => new Set());
    const [name, setName] = useState('');
    const [showNames, setShowNames] = useState(true);
    const [query, setQuery] = useState('');
    const [hoverId, setHoverId] = useState(null);
    const [format, setFormat] = useState('png');
    const [exporting, setExporting] = useState(false);
    const [fontTick, setFontTick] = useState(0);

    const canvasRef = useRef(null);
    const stageRef = useRef(null);
    // The room the card has, so it can be fitted to the panel (which is stretched to
    // the height of the list beside it) instead of overflowing or leaving a gap.
    const [stage, setStage] = useState({ w: 0, h: 0, wide: true });
    const rootRef = useRef(null);
    const loadedRef = useRef(false);

    useEffect(() => {
        const el = stageRef.current;
        if (!el) return undefined;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            // Below lg the panel is as tall as its content, so only the width constrains.
            setStage({ w: rect.width, h: rect.height, wide: window.matchMedia('(min-width: 1024px)').matches });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, []);

    // Back where they left off: a travel map is filled in over weeks, not one sitting.
    useEffect(() => {
        const saved = readSaved();
        if (saved) {
            setVisited(saved.visited);
            setName(saved.name);
            setShowNames(saved.showNames);
        }
        loadedRef.current = true;
    }, []);

    useEffect(() => {
        if (!loadedRef.current) return;
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
                visited: [...visited], name, showNames,
            }));
        } catch {
            /* private mode: the map just will not be remembered */
        }
    }, [visited, name, showNames]);

    // The card is drawn in the page's own font; redraw once it has actually arrived.
    useEffect(() => {
        let alive = true;
        document.fonts?.ready?.then(() => { if (alive) setFontTick((n) => n + 1); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const count = visited.size;
    const divisionsVisited = useMemo(
        () => BY_DIVISION.filter((d) => d.districts.some((x) => visited.has(x.id))).length,
        [visited],
    );
    const percent = Math.round((count / TOTAL) * 100);

    const buildOptions = useCallback(() => ({
        visited,
        title: name.trim() || t('bdTravelMap.defaultTitle'),
        kicker: t('bdTravelMap.kicker'),
        showNames,
        lang,
        fontFamily: rootRef.current
            ? window.getComputedStyle(rootRef.current).fontFamily
            : 'sans-serif',
        labels: {
            countText: num(count),
            totalText: `/${num(TOTAL)}`,
            percentText: t('bdTravelMap.percentText', { percent: num(percent) }),
            partsText: t('bdTravelMap.partsText', { districts: num(count), divisions: num(divisionsVisited) }),
            brand: t('bdTravelMap.brand'),
            site: 'sohozkaj.com',
        },
    }), [visited, name, t, showNames, lang, num, count, percent, divisionsVisited]);

    // Live preview: the same drawing the download uses, at screen resolution.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = CARD.width * ratio;
        canvas.height = CARD.height * ratio;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        drawTravelMap(ctx, buildOptions());
    }, [buildOptions, fontTick]);

    const toggle = useCallback((id) => {
        setVisited((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const setMany = useCallback((ids, on) => {
        setVisited((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
            return next;
        });
    }, []);

    const handleDownload = useCallback(async () => {
        if (exporting) return;
        recordToolUsage('general/bangladesh-travel-map', { category: 'general' });
        setExporting(true);
        try {
            await document.fonts?.ready;
            const canvas = renderTravelMapCanvas(buildOptions(), 2);
            const base = 'bangladesh-travel-map';
            if (format === 'pdf') {
                const jpeg = canvas.toDataURL('image/jpeg', 0.95);
                const pdf = new jsPDF({ unit: 'pt', format: [CARD.width, CARD.height], compress: true });
                pdf.addImage(jpeg, 'JPEG', 0, 0, CARD.width, CARD.height);
                downloadBlob(pdf.output('blob'), `${base}.pdf`);
            } else {
                const { blob, ext } = await encodeCanvas(canvas, format, { quality: 0.95 });
                downloadBlob(blob, `${base}.${ext}`);
            }
        } catch {
            notifications.show({ title: t('common.error'), message: t('bdTravelMap.exportFailed'), color: 'red' });
        } finally {
            setExporting(false);
        }
    }, [exporting, buildOptions, format, t]);

    const q = query.trim().toLowerCase();
    const matches = useCallback(
        (d) => !q || d.bn.toLowerCase().includes(q) || d.en.toLowerCase().includes(q),
        [q],
    );

    const status = deriveToolStatus({
        hasInput: count > 0,
        canRun: true,
        processing: exporting,
        hasResult: count > 0,
        hasError: false,
    });
    const statusText = getToolStatusText(status, t, {
        empty: t('bdTravelMap.statusEmpty'),
        ready: t('bdTravelMap.statusEmpty'),
        success: t('bdTravelMap.statusSuccess', { count: num(count) }),
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const fit = (() => {
        if (!stage.w) return { width: '100%', height: undefined };
        const byWidth = stage.w / CARD.width;
        const byHeight = stage.wide && stage.h > 0 ? stage.h / CARD.height : Infinity;
        const k = Math.min(byWidth, byHeight);
        return { width: Math.floor(CARD.width * k), height: Math.floor(CARD.height * k) };
    })();

    const hovered = hoverId ? BD_DISTRICTS.find((d) => d.id === hoverId) : null;
    const hoveredName = hovered ? (lang === 'en' ? hovered.en : hovered.bn) : '';

    return (
        <ToolPageLayout
            slug="general/bangladesh-travel-map"
            subtitleKey="bdTravelMap.subtitle"
            status={status}
            statusText={statusText}
            howToSteps={howToSteps}
            controlsTitle={t('bdTravelMap.listTitle')}
            matchSidebarHeight
            matchSidebarMinHeight="min(820px, 86vh)"
            onReset={count > 0 || name ? () => { setVisited(new Set()); setName(''); } : undefined}
            resetLabel={t('bdTravelMap.reset')}
            primaryAction={{
                label: t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${format}`) }),
                onClick: handleDownload,
                disabled: exporting,
                loading: exporting,
                icon: IconDownload,
            }}
            actionExtra={(
                <OutputFormatSelect
                    formats={PAGE_OUTPUT_FORMATS}
                    value={format}
                    onChange={setFormat}
                    disabled={exporting}
                />
            )}
            controls={(
                <div ref={rootRef} className="flex min-h-0 flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <span className="rounded-full bg-primary-50 px-2.5 py-1 text-[12px] font-bold tabular-nums text-primary-700">
                            {num(count)} / {num(TOTAL)}
                        </span>
                        <div className="flex items-center gap-3 text-[12px] font-semibold">
                            <button
                                type="button"
                                onClick={() => setMany(BD_DISTRICTS.map((d) => d.id), true)}
                                className="text-primary underline-offset-2 hover:underline"
                            >
                                {t('bdTravelMap.selectAll')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setVisited(new Set())}
                                disabled={!count}
                                className="text-[#6B635C] underline-offset-2 hover:underline disabled:opacity-40 dark:text-zinc-400"
                            >
                                {t('bdTravelMap.clearAll')}
                            </button>
                        </div>
                    </div>

                    <label className="relative block">
                        <IconSearch size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A099]" />
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('bdTravelMap.searchPlaceholder')}
                            className="h-10 w-full rounded-xl border border-[#ECE8E4] bg-white pl-9 pr-3 text-[13px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                    </label>

                    <div className="max-h-[620px] space-y-4 overflow-y-auto pr-1 [scrollbar-width:thin]">
                        {BY_DIVISION.map((division) => {
                            const shown = division.districts.filter(matches);
                            if (!shown.length) return null;
                            const done = division.districts.filter((d) => visited.has(d.id)).length;
                            const allDone = done === division.districts.length;
                            return (
                                <section key={division.id}>
                                    <div className="mb-2 flex items-center justify-between">
                                        <h3 className="text-[13px] font-bold text-[#1C1815] dark:text-zinc-100">
                                            {t('bdTravelMap.division', { name: lang === 'en' ? division.en : division.bn })}
                                            <span className="ml-1.5 text-[11px] font-semibold text-[#A8A099]">
                                                {num(done)}/{num(division.districts.length)}
                                            </span>
                                        </h3>
                                        <button
                                            type="button"
                                            onClick={() => setMany(division.districts.map((d) => d.id), !allDone)}
                                            className="text-[11.5px] font-semibold text-primary underline-offset-2 hover:underline"
                                        >
                                            {allDone ? t('bdTravelMap.divisionClear') : t('bdTravelMap.divisionAll')}
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {shown.map((d) => {
                                            const on = visited.has(d.id);
                                            return (
                                                <button
                                                    key={d.id}
                                                    type="button"
                                                    onClick={() => toggle(d.id)}
                                                    aria-pressed={on}
                                                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
                                                        on
                                                            ? 'border-primary bg-primary text-white'
                                                            : 'border-[#ECE8E4] bg-[#F7F4EF] text-[#3D3833] hover:border-primary/50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200'
                                                    }`}
                                                >
                                                    {on ? <IconCheck size={13} stroke={2.6} /> : null}
                                                    {lang === 'en' ? d.en : d.bn}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </div>
            )}
            settings={(
                <ToolCard>
                    <div className="space-y-4">
                        <input
                            type="text"
                            value={name}
                            maxLength={40}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('bdTravelMap.namePlaceholder')}
                            className="h-10 w-full rounded-xl border border-[#ECE8E4] bg-white px-3 text-[13px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />

                        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[#3D3833] dark:text-zinc-200">
                            <input
                                type="checkbox"
                                checked={showNames}
                                onChange={(e) => setShowNames(e.target.checked)}
                                className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary/30"
                            />
                            {t('bdTravelMap.showNames')}
                        </label>
                    </div>
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('bdTravelMap.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('bdTravelMap.statusEmpty')}
                    showIdle={false}
                    tall
                    fill
                    flushContent
                >
                    <div className="flex min-h-0 flex-1 flex-col gap-2 rounded-lg bg-[#FBFAF9] p-3 dark:bg-zinc-950 sm:p-4">
                        <div ref={stageRef} className="flex min-h-0 flex-1 items-center justify-center">
                            <div
                                className="relative overflow-hidden rounded-2xl shadow-[0_6px_28px_rgba(28,24,21,0.12)]"
                                style={{ width: fit.width, height: fit.height, aspectRatio: `${CARD.width} / ${CARD.height}` }}
                            >
                                <canvas ref={canvasRef} className="block h-full w-full" />
                                {/* Clickable districts, laid over the canvas in card coordinates. */}
                                <svg
                                    viewBox={`0 0 ${CARD.width} ${CARD.height}`}
                                    className="absolute inset-0 h-full w-full"
                                    onPointerLeave={() => setHoverId(null)}
                                >
                                    <g transform={`translate(${MAP_PLACEMENT.x} ${MAP_PLACEMENT.y}) scale(${MAP_PLACEMENT.scale})`}>
                                        {BD_DISTRICTS.map((d) => (
                                            <path
                                                key={d.id}
                                                d={d.d}
                                                fill={hoverId === d.id ? 'rgba(0,0,0,0.14)' : 'transparent'}
                                                style={{ cursor: 'pointer' }}
                                                onPointerEnter={() => setHoverId(d.id)}
                                                onClick={() => toggle(d.id)}
                                            />
                                        ))}
                                    </g>
                                </svg>
                                {hoveredName ? (
                                    <span className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-[#1C1815]/85 px-3 py-1 text-[12px] font-semibold text-white">
                                        {hoveredName}{visited.has(hoverId) ? ' ✓' : ''}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <p className="flex-none text-center text-[12px] text-[#8A7F73] dark:text-zinc-500">{t('bdTravelMap.tip')}</p>
                    </div>
                </ToolPreviewPanel>
            )}
        />
    );
}
